/*
  VERSION GUARD: reload a tab that is running an out-of-date copy of a script.

  A tab keeps running the scripts it loaded, however long it stays open. On
  09/23/2026 an old tab still running yesterday's saved-cart.js kept saving
  the bag without its Rebuy lines every couple of seconds, a new tab kept
  putting them back, and a phone lost the line in between. It only stopped
  once every tab was reloaded by hand. This makes that automatic, for any
  feature, not just the saved cart.

  HOW IT KNOWS. Shopify changes the ?v= part of an asset's address whenever
  the file changes. sections/asset-versions.liquid prints the current address
  of every file on its list, and this script fetches that through the Section
  Rendering API (/?sections=asset-versions) and compares it with the scripts
  this page actually loaded. Files the page did not load are ignored, so a
  guest is never reloaded over the signed-in-only saved cart.

  WHEN IT CHECKS. When the shopper comes back to the tab (it becomes visible,
  the window gains focus, it is restored from the back-forward cache or woken
  from sleep), at most once every FOCUS_GAP_MS. A feature that saves data can
  also ask for a check itself while the tab is hidden: see "For features".

  WHAT IT DOES. The first time it finds a file out of date it fires
  'theme:stale' on document, detail { files }, so a feature can stop writing
  at once. A tab being looked at then reloads, unless someone is typing in
  it; a hidden tab reloads the next time it is looked at. One reload per new
  version per tab session: if the CDN hands back the old file straight after
  a reload, the tab stays as it is instead of looping.

  TO VERSION A NEW FEATURE. Add its file name to the list in
  sections/asset-versions.liquid. Nothing else. Leave out any file whose
  script tag adds its own query string (theme-v1.0.1.js appends &v=), since
  its address would never match the plain asset_url and it would read as
  out of date forever.

  FOR FEATURES. window.themeVersionGuard.check() resolves to true once any
  loaded file is out of date; isStale() answers without a request. Listen for
  'theme:stale' to stop work that must not run on old code.
*/
(function () {
  'use strict';

  if (window.themeVersionGuard || typeof window.fetch !== 'function') return;

  var SECTION = 'asset-versions';
  var FOCUS_GAP_MS = 30 * 1000;
  var RELOAD_KEY = 'version-guard:reloaded-for';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var stale = false;
  var staleFiles = [];
  var latestKey = '';
  var lastCheck = Date.now();
  var inFlight = null;

  // The path and ?v= of an address, so https: and // forms compare equal.
  function srcKey(src) {
    if (!src) return '';
    try {
      var url = new URL(src, window.location.href);
      return url.pathname + url.search;
    } catch (e) {
      return '';
    }
  }

  function fileOf(key) {
    return key.split('?')[0].split('/').pop();
  }

  // Every theme script this page loaded, by file name. Read once: deferred
  // scripts run after the whole page is parsed, so every tag is here already.
  var loaded = {};
  Array.prototype.forEach.call(document.querySelectorAll('script[src]'), function (script) {
    var key = srcKey(script.src);
    if (key.indexOf('/assets/') !== -1) loaded[fileOf(key)] = key;
  });

  function markStale(files, current) {
    stale = true;
    staleFiles = files;
    latestKey = files.map(function (name) { return srcKey(current[name]); }).join('|');
    document.dispatchEvent(new CustomEvent('theme:stale', { detail: { files: files.slice() } }));
  }

  // Resolves to true once any loaded file is known to be out of date. A
  // failed check changes nothing; the next one tries again.
  function check() {
    if (stale) return Promise.resolve(true);
    if (inFlight) return inFlight;
    lastCheck = Date.now();
    inFlight = window.fetch(ROOT + '?sections=' + SECTION, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    }).then(function (body) {
      var html = (body && body[SECTION]) || '';
      var match = /<script[^>]*data-asset-versions[^>]*>([\s\S]*?)<\/script>/.exec(html);
      if (!match) return false;
      var current = JSON.parse(match[1]);
      var changed = Object.keys(current).filter(function (name) {
        return loaded[name] && srcKey(current[name]) !== loaded[name];
      });
      if (changed.length) markStale(changed, current);
      return stale;
    }).catch(function () {
      return stale;
    }).then(function (result) {
      inFlight = null;
      return result;
    });
    return inFlight;
  }

  function isTyping() {
    var el = document.activeElement;
    return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
  }

  function reload() {
    if (document.visibilityState !== 'visible' || isTyping()) return;
    try {
      if (window.sessionStorage.getItem(RELOAD_KEY) === latestKey) return;
      window.sessionStorage.setItem(RELOAD_KEY, latestKey);
    } catch (e) {
      // Without sessionStorage there is no way to know a reload already
      // happened, so do not risk a loop.
      return;
    }
    window.location.reload();
  }

  function onLook() {
    if (document.visibilityState !== 'visible') return;
    var due = stale || Date.now() - lastCheck >= FOCUS_GAP_MS;
    (due ? check() : Promise.resolve(false)).then(function (isStale) {
      if (isStale) reload();
    });
  }

  document.addEventListener('visibilitychange', onLook);
  window.addEventListener('focus', onLook);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) onLook();
  });
  document.addEventListener('resume', onLook);

  window.themeVersionGuard = {
    check: check,
    isStale: function () { return stale; },
    staleFiles: function () { return staleFiles.slice(); }
  };
}());
